import { useCallback, useContext, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { Location } from '../../../clients/quipli-service/location/location';
import { TaxCategory } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import { GeneralLedgerAccount, ItemType } from '../../../clients/quipli-service/accounting/accounting';
import { TenantContext } from '../../../context/tenant-context';
import { useProductApiV1 } from '../../../clients/quipli-service/product/product-api-v1';
import { OrderType } from '../../../clients/quipli-service/order/order';
import {
  CreateSaleProductPriceRequest,
  EditSaleProductPriceRequest,
} from '../../../clients/quipli-service/pricing/pricing';
import { toNumber } from 'lodash';
import { UpsertPartServiceRequest, Product } from '../../../clients/quipli-service/product/product';
import { Category } from '../../../clients/quipli-service/category/category';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';
import { DeliveryConfig, ProductDeliveryConfig } from '../../../clients/quipli-service/delivery/delivery';
import { safeRound } from '../../../util/util';
import { isApiError } from '../../../clients/axios-instance';

export type ProductInventoryType = {
  locationId: number;
  currentStock: number;
  soldStock: number;
  purchasePrice: number;
};

export type ProductPricingType = {
  id: string;
  type: 'Sale'; // TODO: can expand to other types when we add all products
  price: number;
  locationId: number | undefined;
};

function buildErrorMessage(base: string, error: any) {
  const additionalMessage = isApiError(error) && error.message ? `: ${error.message}` : '';
  return `${base}${additionalMessage}`;
}

export const useAddProductData = ({
  id = null,
  type = 'Part',
  canEditTax = true,
}: {
  id?: string | null;
  type: 'Part' | 'Service';
  canEditTax: boolean;
}) => {
  const { contextPath } = useContext(TenantContext);
  const {
    listLocations,
    getConfiguration,
    getDeliveryConfiguration,
    getProduct,
    getProductDeliveryConfig,
    updateProductDeliveryConfiguration,
    createPrice,
    listProductPrices,
    deletePrice,
    editPrice,
    updateProduct,
  } = useAPI();
  const { updateProductLocationStocks, listProductLocationStocks, upsertPart, upsertService } = useProductApiV1();
  const { searchTaxCategories } = useTaxApiV1();
  const { listAccounts } = useAccountingApiV1();
  const { assignTaxCategoryToItem, getTaxCategoryForItem } = useTaxApiV1();
  const { listCategories } = useCategoryApiV1();
  const typeLowerCase = type.toLowerCase();

  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [productInventory, setProductInventory] = useState<ProductInventoryType[]>([]);
  const [productPricing, setProductPricing] = useState<ProductPricingType[]>([]);
  const [globalDeliveryConfig, setGlobalDeliveryConfig] = useState<DeliveryConfig>();
  const [productDeliveryConfig, setProductDeliveryConfig] = useState<ProductDeliveryConfig>();

  const [chargeTax, setChargeTax] = useState<boolean>(false);
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>([]);
  const [taxCategoryId, setTaxCategoryId] = useState<string | undefined>();
  const [glAccounts, setGlAccounts] = useState<GeneralLedgerAccount[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitiallyLoading, setIsInitiallyLoading] = useState<boolean>(true);

  // Input-type fields
  const [title, setTitle] = useState<string>('');

  const [productNumber, setProductNumber] = useState<string>('');
  const [purchasePrice, setPurchasePrice] = useState<number>(0);

  const [categories, setCategories] = useState<Category[]>([]);
  const [productCategoryIds, setProductCategoryIds] = useState<number[]>([]);

  const loadPricing = async () => {
    if (id) {
      const productPrices = await listProductPrices(parseInt(id));

      const newPartPricing = productPrices.map((price) => {
        return {
          id: price.id,
          type: 'Sale' as const,
          price: price.price / 100,
          locationId: price.locationIds ? price.locationIds[0] : undefined,
        };
      });

      setProductPricing(newPartPricing);
    }
  };

  const loadDeliveryConfig = async () => {
    const globalDeliveryConf = await getDeliveryConfiguration();
    setGlobalDeliveryConfig(globalDeliveryConf);

    let productDeliveryConf = id ? await getProductDeliveryConfig(parseInt(id)) : undefined;
    if (!productDeliveryConf) {
      productDeliveryConf = {
        productId: 0,
        allowShipping: true,
        allowPickup: true,
        forceWillCall: false,
        flatRate: globalDeliveryConf.flatRate,
        useGoogleCalculation: globalDeliveryConf.useGoogleCalculation,
        googleCalculation: globalDeliveryConf.googleCalculation,
      };
    }

    setProductDeliveryConfig(productDeliveryConf);
  };

  // -- JUST INITIAL LOADING STUFF
  const initLoad = async () => {
    try {
      const locationBody = await listLocations();
      setAllLocations(locationBody.filter((l) => l.isActive));
      const currency = await getConfiguration('CONF_CURRENCY');
      setCurrency(currency?.value ?? 'USD');

      if (canEditTax) {
        const taxCategories = await searchTaxCategories({});
        setTaxCategories(taxCategories);
      }

      const allCategories = await listCategories();
      setCategories(allCategories);

      const glAccounts = await listAccounts();
      setGlAccounts(glAccounts);
      if (id) {
        const product = await getProduct(parseInt(id));
        setTitle(product.name);
        setProductNumber(product.sku ?? '');
        setSelectedLocationIds(product.locationIds);
        setChargeTax(product.taxable);
        setPurchasePrice(product.purchasePrice ?? 0);
        setIsActive(product.isActive);
        setProductCategoryIds(product.categoryIds);

        const locationStocks = await listProductLocationStocks([parseInt(id)]);

        const newProductInventory = await Promise.all(
          locationStocks.map(async (locationStock) => {
            let saleStock: string | number = locationStock.saleStock ?? 0;
            if (typeof saleStock !== 'number') {
              saleStock = toNumber(saleStock);
            }

            return {
              locationId: locationStock.locationId,
              currentStock: saleStock,
              soldStock: 0,
              purchasePrice: 0, // TODO: No purchase price done by location for now, can add later if switch to units
            };
          }),
        );

        setProductInventory(newProductInventory);
        await loadPricing();
        await loadDeliveryConfig();

        if (canEditTax) {
          const taxCategory = await getTaxCategoryForItem({
            itemId: id,
            itemType: ItemType.Product,
          });
          setTaxCategoryId(taxCategory?.taxCategoryId);
        }

        if (product.name === '' || (product.sku === '' && type === 'Part')) {
          toast('Please enter a title and part number before making any other changes.', 'error');
          return;
        }
      }
    } catch {
      toast(`Failed to load initial ${typeLowerCase} data.`, 'error');
    }
  };

  useEffect(() => {
    setIsInitiallyLoading(true);
    setIsLoading(true);
    initLoad().then(() => {
      setIsInitiallyLoading(false);
      setIsLoading(false);
    });
  }, []);

  const createProductCall = async () => {
    try {
      setIsSaving(true);

      const upsertRequest = {
        id: id ? parseInt(id) : undefined,
        name: title,
        type,
        chargeTax: true,
        categoryIds: productCategoryIds,
        isActive,
      };

      let newProduct: Product;
      if (type == 'Part') {
        if (title === '' || productNumber === '') {
          toast('Please enter a name and part number.', 'error');
          return;
        }

        newProduct = await upsertPart({
          ...upsertRequest,
          sku: productNumber,
        });
      } else {
        if (title === '') {
          toast('Please enter a name.', 'error');
          return;
        }

        newProduct = await upsertService(upsertRequest);
      }
      toast(`${type} created successfully.`, 'success');

      // Annoying, but routing to the edit page after showing the success for a moment
      // would rather a full-page spinner but we don't have that pattern used yet
      await new Promise((resolve) => setTimeout(resolve, 500));

      const url = `${contextPath}manager/inventory/${type.toLowerCase()}s/form/${newProduct.id}`;
      window.open(url, '_self')?.focus();
    } catch (e: any) {
      toast(buildErrorMessage(`Failed to create ${typeLowerCase}`, e), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const partBase = {
    id: parseInt(id ?? '0'),
    name: title,
    sku: productNumber,
    type,
    chargeTax,
    taxCategoryId,
    purchasePrice: purchasePrice || undefined,
    isActive,
  };

  const updateProductMetadata = useCallback(
    async (body: UpsertPartServiceRequest) => {
      if (id && !isInitiallyLoading) {
        setIsSaving(true);
        if (type == 'Part') {
          await upsertPart(body);
        } else {
          await upsertService(body);
        }
      }
    },
    [id, isInitiallyLoading],
  );

  const doCallsThenLoad = async (calls: Promise<void>[]) => {
    try {
      setIsSaving(true);
      await Promise.all(calls);
      await initLoad();
    } catch {
      toast(`Failed to update ${type}.`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = async (key: keyof UpsertPartServiceRequest, value: any) => {
    try {
      setIsSaving(true);
      await updateProductMetadata({ ...partBase, [key]: value });
    } catch (error) {
      toast(buildErrorMessage(`Failed to update ${typeLowerCase}`, error), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const assignTaxCategory = async (taxCategoryId: string | undefined) => {
    if (id) {
      await assignTaxCategoryToItem({
        itemId: id,
        itemType: ItemType.Product,
        taxCategoryId: taxCategoryId,
      });
    }
  };

  const updateProductInventory = useCallback(
    async (productInventory: ProductInventoryType[]) => {
      try {
        if (id) {
          if (productInventory.length > 0) {
            await updateProductLocationStocks(parseInt(id), {
              productId: parseInt(id),
              locationStocks: productInventory.map((inventory) => {
                return {
                  productId: parseInt(id),
                  locationId: inventory.locationId,
                  rentalStock: 0,
                  saleStock: inventory.currentStock,
                };
              }),
            });
          } else {
            await updateProductLocationStocks(parseInt(id), {
              productId: parseInt(id),
              locationStocks: allLocations.map((location) => {
                return {
                  productId: parseInt(id),
                  locationId: location.id,
                  rentalStock: 0,
                  saleStock: 0,
                };
              }),
            });
          }
        }
      } catch (e) {
        toast(`Failed to update ${type} inventory information.`, 'error');
      }
    },
    [id, allLocations, type],
  );

  const updateProductPricing = useCallback(
    async (productPricing: ProductPricingType[]) => {
      try {
        if (id) {
          const updatePricesBody: (EditSaleProductPriceRequest & { id: string })[] = productPricing
            .filter((pp) => pp.id !== '')
            .map((pricing) => {
              let locationIds = pricing.locationId ? [pricing.locationId] : undefined;

              // For single location, us all locations for convenience
              if (allLocations.length === 1) {
                locationIds = undefined;
              }

              return {
                productId: parseInt(id),
                type: OrderType.Sale,
                price: safeRound(pricing.price * 100),
                locationIds,
                from: undefined,
                to: undefined,
                name: undefined,
                featured: true,
                id: pricing.id,
              };
            });

          const createPricesBody: CreateSaleProductPriceRequest[] = productPricing
            .filter((pp) => pp.id === '')
            .map((pricing) => {
              return {
                productId: parseInt(id),
                type: OrderType.Sale,
                price: safeRound(pricing.price * 100),
                locationIds: pricing.locationId ? [pricing.locationId] : undefined,
                from: undefined,
                to: undefined,
                name: undefined,
                featured: true,
              };
            });

          updatePricesBody.forEach(async (price) => {
            await editPrice(price.id, price as EditSaleProductPriceRequest);
          });

          createPricesBody.forEach(async (price) => {
            await createPrice(price);
          });
        }
      } catch {
        toast(`Failed to update ${type} pricing information.`, 'error');
      }
    },
    [id, type, productPricing],
  );

  useEffect(() => {
    if (selectedLocationIds.length === 0) {
      setProductInventory([]);
    } else {
      const newProductInventory = selectedLocationIds.map((locationId) => {
        const existingInventory = productInventory.find((inventory) => inventory.locationId === locationId);
        return existingInventory ?? { locationId, currentStock: 0, soldStock: 0, purchasePrice: 0 };
      });
      setProductInventory(newProductInventory);
    }
  }, [selectedLocationIds]);

  // -- ALL DELETION STUFF
  const deleteProductCall = async () => {
    try {
      setIsSaving(true);
      if (id) {
        await updateProduct(parseInt(id), {
          isActive: isActive,
          isDeleted: true,
        });
      }
      toast(`${type} deleted successfully.`, 'success');

      // Annoying, but routing to the edit page after showing the success for a moment
      // would rather a full-page spinner but we don't have that pattern used yet
      await new Promise((resolve) => setTimeout(resolve, 500));

      const url = `${contextPath}manager/inventory/${type.toLowerCase()}s`;
      window.open(url, '_self')?.focus();
    } catch (e) {
      toast(`Failed to delete ${type}.`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const deletePriceCall = async (priceId: string, rowToDelete: number) => {
    try {
      setIsSaving(true);

      if (!priceId || priceId === '') {
        productPricing.splice(rowToDelete, 1);
        setProductPricing([...productPricing]);
        return;
      }
      const existingPrice = productPricing.find((pricing) => pricing.id === priceId);

      if (existingPrice) {
        await deletePrice(priceId);
      }
      await initLoad();
      toast('Price deleted successfully.', 'success');
    } catch (e) {
      toast('Failed to delete price.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const clearAll = () => {
    setTitle('');
    setProductNumber('');
    setSelectedLocationIds([]);
    setProductInventory([]);
    setProductPricing([]);
    setChargeTax(false);
    setTaxCategoryId(undefined);
    setProductCategoryIds([]);
  };

  return {
    title,
    setTitle,
    handleBlur,
    productNumber,
    setProductNumber,
    selectedLocationIds,
    setSelectedLocationIds: async (passedLocationIds: number[]) => {
      let newSelectedLocationIds: number[] = [];

      if (passedLocationIds.length == 1) {
        const newSelectedLocationId = passedLocationIds[0];
        if (selectedLocationIds.includes(newSelectedLocationId)) {
          newSelectedLocationIds = selectedLocationIds.filter((id) => id !== newSelectedLocationId);
        } else {
          newSelectedLocationIds = [...selectedLocationIds, newSelectedLocationId];
        }
      } else {
        newSelectedLocationIds = passedLocationIds;
      }

      let updateCallInventory: ProductInventoryType[] = [];
      await updateProductMetadata({ ...partBase, locationIds: newSelectedLocationIds });
      if (newSelectedLocationIds.length === 0) {
        updateCallInventory = [];
      } else {
        const newProductInventory = newSelectedLocationIds.map((locationId) => {
          const existingInventory = productInventory.find((inventory) => inventory.locationId === locationId);
          return existingInventory ?? { locationId, currentStock: 0, soldStock: 0, purchasePrice: 0 };
        });
        updateCallInventory = newProductInventory;
      }
      await Promise.all([updateProductInventory(updateCallInventory), initLoad()]);
      setIsSaving(false);
    },
    productInventory,
    setProductInventory: async (tableProductInventory: ProductInventoryType, locationId: number) => {
      const newProductInventory = [...productInventory];

      const index = newProductInventory.findIndex((inv) => inv.locationId === locationId);
      newProductInventory[index] = { ...newProductInventory[index], ...tableProductInventory };

      setProductInventory(newProductInventory);
      try {
        setIsSaving(true);
        await Promise.all([updateProductInventory(newProductInventory), initLoad()]);
      } catch {
        toast(`Failed to save ${typeLowerCase} inventory data.`, 'error');
      } finally {
        setIsSaving(false);
      }
    },
    productPricing,
    setProductPricing: async (tableProductPricing: ProductPricingType) => {
      const newProductPricing = [...productPricing];

      const index = newProductPricing.findIndex((pp) => pp.id === tableProductPricing.id);
      if (index === -1) {
        newProductPricing.push(tableProductPricing);
      } else {
        newProductPricing[index] = { ...newProductPricing[index], ...tableProductPricing };
      }

      try {
        setIsSaving(true);
        await Promise.all([updateProductPricing(newProductPricing), initLoad()]);
      } catch {
        toast(`Failed to save ${typeLowerCase} pricing data.`, 'error');
      } finally {
        setIsSaving(false);
      }
    },
    globalDeliveryConfig,
    productDeliveryConfig,
    setProductDeliveryConfig: async (deliveryConfig: ProductDeliveryConfig) => {
      if (!id) return;

      const request = {
        productId: parseInt(id),
        allowPickup: deliveryConfig.allowPickup,
        allowShipping: deliveryConfig.allowShipping,
        forceWillCall: deliveryConfig.forceWillCall,
        rate: deliveryConfig.flatRate,
        baseCost: deliveryConfig.googleCalculation?.baseCost,
        includedUnits: deliveryConfig.googleCalculation?.includedUnits,
        perUnitCost: deliveryConfig.googleCalculation?.perUnitCost,
      };
      try {
        setIsSaving(true);
        await Promise.all([updateProductDeliveryConfiguration(request), initLoad()]);
      } catch {
        toast(`Failed to save ${typeLowerCase} delivery and pickup data.`, 'error');
      } finally {
        setIsSaving(false);
      }
    },
    chargeTax,
    setChargeTax: (value: boolean) => doCallsThenLoad([updateProductMetadata({ ...partBase, chargeTax: value })]),
    taxCategoryId,
    setTaxCategoryId: (value: any) =>
      doCallsThenLoad([updateProductMetadata({ ...partBase, taxCategoryId: value }), assignTaxCategory(value)]),
    clearAll,
    allLocations,
    setAllLocations,
    isSaving: isSaving || isLoading,
    setIsSaving,
    isLoading: isLoading || isInitiallyLoading, // TODO: remove for debugging
    isInitiallyLoading,
    currency,
    taxCategories,
    glAccounts,
    createProductCall,
    deletePriceCall,
    purchasePrice,
    setPurchasePrice,
    deleteProductCall,
    setProductCategoryIds: (value: any) => {
      doCallsThenLoad([updateProductMetadata({ ...partBase, categoryIds: value })]);
    },
    productCategoryIds,
    categories,
    isActive,
    toggleIsActive: () => {
      doCallsThenLoad([updateProductMetadata({ ...partBase, isActive: !isActive })]);
      setIsActive((prev) => !prev);
    },
  };
};
